import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Loader2, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useCreatePortal } from '@/hooks/portal';
import { useAccountBranding } from '@/hooks/branding/useAccountBranding';
import { GenieAvatar } from '@/components/GenieAvatar';
import { GenieBranding, DEFAULT_BRANDING } from '@/types/ui/branding';

interface CreatePortalSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreatePortalSheet = ({ isOpen, onClose }: CreatePortalSheetProps) => {
  const navigate = useNavigate();
  const { mutate: createPortal, isPending } = useCreatePortal();
  const { branding: accountBranding, isLoading: accountBrandingLoading } = useAccountBranding();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showBrandingOptions, setShowBrandingOptions] = useState(false);
  const [useAccountBrandingToggle, setUseAccountBrandingToggle] = useState(true);
  const [customBranding, setCustomBranding] = useState<GenieBranding>(DEFAULT_BRANDING);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    createPortal(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        welcome_message: welcomeMessage.trim() || undefined,
        // If using account branding, don't pass branding (will be null/undefined)
        // If using custom branding, pass the custom branding object
        branding: useAccountBrandingToggle ? undefined : customBranding,
      },
      {
        onSuccess: (portal) => {
          onClose();
          resetForm();
          // Navigate to the portal detail page to add genies
          if (portal) {
            navigate(`/genie-hub/portals/${portal.id}`);
          }
        },
      }
    );
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setWelcomeMessage('');
    setShowBrandingOptions(false);
    setUseAccountBrandingToggle(true);
    setCustomBranding(DEFAULT_BRANDING);
  };

  const handleClose = () => {
    if (!isPending) {
      resetForm();
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col h-full">
        <SheetHeader>
          <SheetTitle>Create New Portal</SheetTitle>
          <SheetDescription>
            Create a portal to group multiple genies together on a single public page.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col mt-6">
          <div className="space-y-6 flex-1">
            <div className="space-y-2">
              <Label htmlFor="name">Portal Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Customer Support Hub"
                disabled={isPending}
                required
              />
              <p className="text-xs text-muted-foreground">
                This will be displayed at the top of your portal page.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this portal offers..."
                rows={3}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="welcomeMessage">Welcome Message</Label>
              <Textarea
                id="welcomeMessage"
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                placeholder="Welcome! Choose a genie below to get started..."
                rows={2}
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">
                Displayed to visitors when they land on your portal.
              </p>
            </div>

            {/* Branding Section (Collapsible) */}
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowBrandingOptions(!showBrandingOptions)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                disabled={isPending}
              >
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-[#4E9CFF]" />
                  <span className="font-medium text-gray-700">Branding</span>
                  <span className="text-xs text-gray-500">
                    {useAccountBrandingToggle ? '(Using account branding)' : '(Custom)'}
                  </span>
                </div>
                {showBrandingOptions ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
              </button>

              {showBrandingOptions && (
                <div className="p-4 space-y-4 border-t">
                  {/* Account Branding Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Use Account Branding</Label>
                      <p className="text-xs text-muted-foreground">
                        {accountBranding
                          ? 'Inherit colors from your account settings'
                          : 'No account branding configured'}
                      </p>
                    </div>
                    <Switch
                      checked={useAccountBrandingToggle}
                      onCheckedChange={setUseAccountBrandingToggle}
                      disabled={isPending || !accountBranding}
                    />
                  </div>

                  {/* Account Branding Preview */}
                  {useAccountBrandingToggle && accountBranding && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12">
                        <GenieAvatar
                          color={accountBranding.primaryColor}
                          secondaryColor={accountBranding.secondaryColor}
                          gradientEnabled={accountBranding.gradientEnabled}
                          gradientAngle={accountBranding.gradientAngle}
                          avatarBackgroundOpacity={accountBranding.avatarBackgroundOpacity}
                          customAvatarUrl={accountBranding.logoUrl || accountBranding.customAvatarUrl}
                          size={48}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded-full border border-gray-300"
                          style={{ backgroundColor: accountBranding.primaryColor }}
                        />
                        {accountBranding.secondaryColor && accountBranding.gradientEnabled && (
                          <div
                            className="w-5 h-5 rounded-full border border-gray-300"
                            style={{ backgroundColor: accountBranding.secondaryColor }}
                          />
                        )}
                        <span className="text-sm text-gray-600">
                          {accountBranding.gradientEnabled ? 'Gradient' : 'Solid'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Custom Branding Options */}
                  {!useAccountBrandingToggle && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="primaryColor">Primary Color</Label>
                          <div className="flex gap-2">
                            <Input
                              id="primaryColor"
                              type="color"
                              value={customBranding.primaryColor}
                              onChange={(e) =>
                                setCustomBranding({ ...customBranding, primaryColor: e.target.value })
                              }
                              className="w-12 h-10 p-1 cursor-pointer"
                              disabled={isPending}
                            />
                            <Input
                              type="text"
                              value={customBranding.primaryColor}
                              onChange={(e) =>
                                setCustomBranding({ ...customBranding, primaryColor: e.target.value })
                              }
                              className="flex-1"
                              disabled={isPending}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="secondaryColor">Secondary Color</Label>
                          <div className="flex gap-2">
                            <Input
                              id="secondaryColor"
                              type="color"
                              value={customBranding.secondaryColor || '#000000'}
                              onChange={(e) =>
                                setCustomBranding({
                                  ...customBranding,
                                  secondaryColor: e.target.value,
                                  gradientEnabled: true,
                                })
                              }
                              className="w-12 h-10 p-1 cursor-pointer"
                              disabled={isPending}
                            />
                            <Input
                              type="text"
                              value={customBranding.secondaryColor || ''}
                              onChange={(e) =>
                                setCustomBranding({
                                  ...customBranding,
                                  secondaryColor: e.target.value || null,
                                })
                              }
                              placeholder="#000000"
                              className="flex-1"
                              disabled={isPending}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="gradientEnabled">Enable Gradient</Label>
                        <Switch
                          id="gradientEnabled"
                          checked={customBranding.gradientEnabled || false}
                          onCheckedChange={(checked) =>
                            setCustomBranding({ ...customBranding, gradientEnabled: checked })
                          }
                          disabled={isPending}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        You can configure more branding options after creating the portal.
                      </p>
                    </div>
                  )}

                  {/* No account branding message */}
                  {useAccountBrandingToggle && !accountBranding && !accountBrandingLoading && (
                    <p className="text-sm text-gray-500">
                      Set up account branding in Account Settings to use across all portals.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <SheetFooter className="mt-6 pt-6 border-t">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Portal'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default CreatePortalSheet;
